import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadDocument, type IDocumentVersion } from '@apis/documents';
import { StatusPill } from '../../StatusPill/StatusPill';
import styles from './VersionEntry.module.css';
import { ClockIcon, DownloadIcon, LoaderIcon, PencilIcon, PersonIcon, TrashIcon } from '@/utils/icons';
import { formatProjectDateTime } from '@/utils/formatProjectDate';

export interface IVersionEntryProps {
  entry: IDocumentVersion;
  isLatest: boolean;
  siteId: string;
  downloadName: string;
  tz: string | undefined; // The project's zone — undefined only while the working site resolves
  onDelete?: (version: IDocumentVersion) => void; // When absent = the entry can't be deleted
  onMarkup?: (version: IDocumentVersion) => void; // When absent = the version can't be marked up
  onSubmit?: (version: IDocumentVersion) => void;
  // Resolves false when the decision failed, so the form stays open with the
  // typed comment rather than closing over an error the user must re-do.
  onDecide?: (version: IDocumentVersion, decision: 'approve' | 'reject', comment: string) => Promise<boolean>;
  isBusy?: boolean; // True while this version's own approval request is in flight.
}

export function VersionEntry({ entry, isLatest, siteId, downloadName, tz, onDelete, onMarkup, onSubmit, onDecide, isBusy = false }: IVersionEntryProps) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const [approvalDecision, setApprovalDecision] = useState<'approve' | 'reject' | null>(null);
  const [decisionComment, setDecisionComment] = useState('');

  async function handleDownload() {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadDocument(siteId, entry.fileId, downloadName);
    } catch (err) {
      console.error('version download failed', err);
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  }

  function closeDecision() {
    setApprovalDecision(null);
    setDecisionComment('');
  }

  // The form is the only thing on screen tied to this request, so it stays put
  // — spinner and all — until the drawer actually shows the new state. Closing
  // on click would drop the user back to the pre-decision row with no sign
  // anything was happening.
  async function confirmDecision() {
    if (approvalDecision === null || isBusy) return;
    if (await onDecide!(entry, approvalDecision, decisionComment.trim())) closeDecision();
  }

  const canConfirmDecision = approvalDecision === 'approve' || decisionComment.trim() !== '';
  const showSubmit = !!onSubmit && entry.canSubmit;
  const showDecisions = !!onDecide && (entry.canApprove || entry.canReject);

  return (
    <div className={styles.versionEntry}>
      <div className={styles.versionTimeline}>
        <span className={`${styles.versionDot} ${isLatest ? styles.versionDotLatest : ''}`} />
        <span className={styles.versionLine} />
      </div>
      <div className={styles.versionBody}>
        <div className={styles.versionHeader}>
          <span className={styles.versionNum}>{t('documents.versionShort', { version: entry.version })}</span>
          <StatusPill status={entry.status} />
          <span className={styles.versionActions}>
            <button
              type="button"
              className={styles.versionActionBtn}
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              aria-busy={isDownloading}
              aria-label={t('documents.downloadVersion', { version: entry.version })}
              title={t('documents.downloadVersion', { version: entry.version })}
            >
              <DownloadIcon size={14} />
            </button>
            {onMarkup && (
              <button
                type="button"
                className={styles.versionActionBtn}
                onClick={() => onMarkup(entry)}
                aria-label={t('documents.markup.open', { version: entry.version })}
                title={t('documents.markup.open', { version: entry.version })}
              >
                <PencilIcon size={14} />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className={styles.versionActionBtn}
                onClick={() => onDelete(entry)}
                aria-label={t('documents.deleteVersionAria', { version: entry.version })}
                title={t('documents.deleteVersionAria', { version: entry.version })}
              >
                <TrashIcon size={14} />
              </button>
            )}
          </span>
        </div>
        {entry.description.trim() !== '' && <p className={styles.versionDesc}>{entry.description}</p>}

        {entry.decision && (
          <p className={`${styles.decision} ${entry.status === 'rejected' ? styles.decisionRejected : ''}`}>
            <span className={styles.decisionMeta}>
              {entry.decision.supersededBySeq !== undefined
                // Closed by a newer version being approved, not by anyone deciding on it
                ? t('documents.approval.closedBySupersede', {
                    version: entry.decision.supersededBySeq,
                    name: entry.decision.by,
                    date: tz ? formatProjectDateTime(entry.decision.at, tz) : '—',
                  })
                : t(`documents.approval.decidedBy.${entry.status === 'rejected' ? 'rejected' : 'approved'}`, {
                    name: entry.decision.by,
                    date: tz ? formatProjectDateTime(entry.decision.at, tz) : '—',
                  })}
            </span>
            {entry.decision.comment && <span className={styles.decisionComment}>{entry.decision.comment}</span>}
          </p>
        )}

        {(showSubmit || showDecisions) && (
          <div className={styles.approvalActions}>
            {showSubmit && (
              <button
                type="button"
                className={styles.approvalBtn}
                onClick={() => onSubmit!(entry)}
                disabled={isBusy}
                aria-busy={isBusy}
              >
                {t('documents.approval.submit')}
              </button>
            )}
            {showDecisions && approvalDecision === null && (
              <>
                {entry.canApprove && (
                  <button
                    type="button"
                    className={`${styles.approvalBtn} ${styles.approveBtn}`}
                    onClick={() => setApprovalDecision('approve')}
                    disabled={isBusy}
                  >
                    {t('documents.approval.approve')}
                  </button>
                )}
                {entry.canReject && (
                  <button
                    type="button"
                    className={`${styles.approvalBtn} ${styles.rejectBtn}`}
                    onClick={() => setApprovalDecision('reject')}
                    disabled={isBusy}
                  >
                    {t('documents.approval.reject')}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {approvalDecision !== null && (
          <div className={styles.decisionForm}>
            <label className={styles.decisionLabel} htmlFor={`decision-comment-${entry.version}`}>
              {t(`documents.approval.commentLabel.${approvalDecision}`)}
            </label>
            <textarea
              id={`decision-comment-${entry.version}`}
              className={styles.decisionInput}
              value={decisionComment}
              autoFocus
              rows={2}
              onChange={(e) => setDecisionComment(e.target.value)}
              onKeyDown={(e) => {
                // Escape backs out of the decision only — it never reaches the drawer.
                if (e.key === 'Escape') { e.stopPropagation(); closeDecision(); }
              }}
              placeholder={t(`documents.approval.commentPlaceholder.${approvalDecision}`)}
              disabled={isBusy}
            />
            <div className={styles.decisionFormActions}>
              <button type="button" className={styles.decisionCancel} onClick={closeDecision} disabled={isBusy}>
                {t('documents.approval.cancel')}
              </button>
              <button
                type="button"
                className={`${styles.approvalBtn} ${approvalDecision === 'approve' ? styles.approveBtn : styles.rejectBtn}`}
                onClick={() => void confirmDecision()}
                disabled={!canConfirmDecision || isBusy}
                aria-busy={isBusy}
              >
                {isBusy && <span className={styles.decisionSpinner}><LoaderIcon /></span>}
                {t(`documents.approval.confirm.${approvalDecision}`)}
              </button>
            </div>
          </div>
        )}

        {downloadFailed && (
          <p className={styles.downloadError} role="alert">{t('documents.downloadError')}</p>
        )}

        <div className={styles.versionMeta}>
          <span className={styles.versionMetaItem}>
            <PersonIcon />
            {entry.author}
          </span>
          <span className={styles.versionMetaItem}>
            <ClockIcon />
            {tz ? formatProjectDateTime(entry.date, tz) : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}